import { useState, useEffect } from 'react';
import { Save, Send, Truck, AlertTriangle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DriverLog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logId, scheduleId } = location.state || {};
  
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [formData, setFormData] = useState({
    vehicle_id: '',
    start_mileage: '',
    end_mileage: '',
    notes: '',
    truck_washed_inside: false,
    truck_washed_outside: false,
    had_accident: false,
    was_third_party_involved: false,
    accident_third_party_name: '',
    accident_contact_number: '',
    accident_registration: '',
    accident_images: []
  });

  useEffect(() => {
    if (logId) {
      fetchLogData();
    } else {
      fetchVehicles();
    }
  }, [logId]);

  const fetchLogData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch log data
      const logRes = await fetch(`${BACKEND_URL}/api/driver-logs/${logId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!logRes.ok) {
        const error = await logRes.json();
        throw new Error(error.detail || 'Failed to load log');
      }

      const logData = await logRes.json();
      
      // Fetch vehicles
      const vehiclesRes = await fetch(`${BACKEND_URL}/api/vehicles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const vehiclesData = await vehiclesRes.json();

      setVehicles(vehiclesData);
      setFormData({
        vehicle_id: logData.vehicle_id || '',
        start_mileage: logData.start_mileage || '',
        end_mileage: logData.end_mileage || '',
        notes: logData.notes || '',
        truck_washed_inside: logData.truck_washed_inside || false,
        truck_washed_outside: logData.truck_washed_outside || false,
        had_accident: logData.had_accident || false,
        was_third_party_involved: logData.was_third_party_involved || false,
        accident_third_party_name: logData.accident_third_party_name || '',
        accident_contact_number: logData.accident_contact_number || '',
        accident_registration: logData.accident_registration || '',
        accident_images: logData.accident_images || []
      });
      
      console.log('Log data loaded:', { vehicle_id: logData.vehicle_id, vehicles: vehiclesData.length });
    } catch (error) {
      console.error('Error loading log:', error);
      toast.error(`Failed to load log data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/vehicles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setVehicles(data);
    } catch (error) {
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const token = localStorage.getItem('token');
    const uploadPromises = files.map(async (file) => {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      try {
        const response = await fetch(`${BACKEND_URL}/api/driver-logs/upload-accident-image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataToUpload
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        const data = await response.json();
        return data.file_path;
      } catch (error) {
        console.error('Error uploading image:', error);
        throw error;
      }
    });

    try {
      const uploadedPaths = await Promise.all(uploadPromises);
      setFormData(prev => ({
        ...prev,
        accident_images: [...prev.accident_images, ...uploadedPaths]
      }));
      toast.success(`${files.length} image(s) uploaded successfully`);
    } catch (error) {
      toast.error('Failed to upload images');
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      accident_images: prev.accident_images.filter((_, i) => i !== index)
    }));
  };

  const handleSaveDraft = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/driver-logs/${logId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          status: 'draft'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save draft');
      }

      toast.success('Draft saved successfully');
      navigate('/profile', { state: { refreshData: true } });
    } catch (error) {
      toast.error('Failed to save draft');
    }
  };

  const handleSubmitAndClockOut = async () => {
    if (!formData.vehicle_id || !formData.start_mileage || !formData.end_mileage) {
      toast.error('Please fill in vehicle and mileage fields');
      return;
    }

    if (parseInt(formData.end_mileage) < parseInt(formData.start_mileage)) {
      toast.error('End mileage cannot be less than start mileage');
      return;
    }

    // Validate accident fields if 3rd party is involved
    if (formData.had_accident && formData.was_third_party_involved) {
      if (!formData.accident_third_party_name || !formData.accident_contact_number) {
        toast.error('Please fill in all required 3rd party details (name and contact number)');
        return;
      }
      if (!formData.accident_images || formData.accident_images.length === 0) {
        toast.error('Please upload at least one accident image');
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/driver-logs/${logId}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit log');
      }

      toast.success('Log submitted and clocked out successfully');
      navigate('/profile', { state: { refreshData: true } });
    } catch (error) {
      toast.error('Failed to submit log');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-4 sm:py-8 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col gap-4">
            <Button
              onClick={() => navigate('/profile/driver-schedules')}
              variant="ghost"
              size="sm"
              className="self-start"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Schedules
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Driver Log</h1>
              <p className="text-sm text-gray-600 mt-1">Complete your route information</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
          <div className="space-y-6">
            {/* Vehicle Selection */}
            <div>
              <Label htmlFor="vehicle_id">Vehicle</Label>
              <select
                id="vehicle_id"
                value={formData.vehicle_id}
                onChange={(e) => setFormData({...formData, vehicle_id: e.target.value})}
                className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Select a vehicle</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.registration} ({vehicle.vehicle_type})
                  </option>
                ))}
              </select>
            </div>

            {/* Mileage */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_mileage">Start Mileage</Label>
                <Input
                  id="start_mileage"
                  type="number"
                  placeholder="e.g., 45000"
                  value={formData.start_mileage}
                  onChange={(e) => setFormData({...formData, start_mileage: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="end_mileage">End Mileage</Label>
                <Input
                  id="end_mileage"
                  type="number"
                  placeholder="e.g., 45250"
                  value={formData.end_mileage}
                  onChange={(e) => setFormData({...formData, end_mileage: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Truck Washed */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Truck Washed</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="truck_washed_inside"
                    checked={formData.truck_washed_inside}
                    onChange={(e) => setFormData({...formData, truck_washed_inside: e.target.checked})}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <Label htmlFor="truck_washed_inside" className="cursor-pointer flex items-center gap-2 text-blue-900 font-medium">
                    <Truck className="w-5 h-5" />
                    Inside
                  </Label>
                </div>
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="truck_washed_outside"
                    checked={formData.truck_washed_outside}
                    onChange={(e) => setFormData({...formData, truck_washed_outside: e.target.checked})}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <Label htmlFor="truck_washed_outside" className="cursor-pointer flex items-center gap-2 text-blue-900 font-medium">
                    <Truck className="w-5 h-5" />
                    Outside
                  </Label>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes / Issues</Label>
              <Textarea
                id="notes"
                placeholder="Report any issues, delays, or important information..."
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={4}
                className="mt-1"
              />
            </div>

            {/* Accident Checkbox */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                <input
                  type="checkbox"
                  id="had_accident"
                  checked={formData.had_accident}
                  onChange={(e) => setFormData({...formData, had_accident: e.target.checked, was_third_party_involved: false})}
                  className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <Label htmlFor="had_accident" className="cursor-pointer flex items-center gap-2 text-red-900 font-semibold">
                  <AlertTriangle className="w-5 h-5" />
                  I had an accident during this schedule
                </Label>
              </div>

              {/* Second Checkbox - Was 3rd Party Involved */}
              {formData.had_accident && (
                <div className="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="was_third_party_involved"
                    checked={formData.was_third_party_involved}
                    onChange={(e) => setFormData({...formData, was_third_party_involved: e.target.checked})}
                    className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <Label htmlFor="was_third_party_involved" className="cursor-pointer text-orange-900 font-semibold">
                    Was a 3rd party involved?
                  </Label>
                </div>
              )}

              {/* Conditional 3rd Party Fields */}
              {formData.had_accident && formData.was_third_party_involved && (
                <div className="space-y-4 p-4 bg-red-50 border border-red-300 rounded-lg">
                  <h3 className="font-semibold text-red-900 mb-3">3rd Party Details</h3>
                  
                  <div>
                    <Label htmlFor="accident_third_party_name">
                      3rd Party Name <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      id="accident_third_party_name"
                      type="text"
                      placeholder="Full name of the other party"
                      value={formData.accident_third_party_name}
                      onChange={(e) => setFormData({...formData, accident_third_party_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="accident_contact_number">
                      Contact Number <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      id="accident_contact_number"
                      type="tel"
                      placeholder="Phone number"
                      value={formData.accident_contact_number}
                      onChange={(e) => setFormData({...formData, accident_contact_number: e.target.value})}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="accident_registration">
                      Registration
                    </Label>
                    <Input
                      id="accident_registration"
                      type="text"
                      placeholder="Vehicle registration number (optional)"
                      value={formData.accident_registration}
                      onChange={(e) => setFormData({...formData, accident_registration: e.target.value})}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="accident_images">
                      Upload Images <span className="text-red-600">*</span>
                    </Label>
                    <input
                      id="accident_images"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="mt-1 w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-red-100 file:text-red-700 hover:file:bg-red-200"
                    />
                    <p className="text-xs text-gray-500 mt-1">Upload photos of the accident scene (required)</p>
                    
                    {formData.accident_images.length > 0 && (
                      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {formData.accident_images.map((imgPath, index) => (
                          <div key={index} className="relative group">
                            <img 
                              src={`${BACKEND_URL}/${imgPath}`} 
                              alt={`Accident ${index + 1}`} 
                              className="w-full h-24 object-cover rounded border border-gray-300"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                onClick={handleSaveDraft}
                variant="outline"
                className="flex-1 w-full"
              >
                <Save className="w-4 h-4 mr-2" />
                Save as Draft
              </Button>
              <Button
                onClick={handleSubmitAndClockOut}
                className="flex-1 w-full bg-teal-600 hover:bg-teal-700"
              >
                <Send className="w-4 h-4 mr-2" />
                Submit to Manager & Clock Out
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverLog;
